<template>
 <section class="kpis-section" ref="kpisSection" :aria-label="$t('kpisWebeasy.eyebrow')">
   <div class="kpis-panel">
     <div class="kpis-intro">
       <h3 class="kpis-eyebrow gradient-text fade-in-section" style="--delay: 0s;">{{ $t('kpisWebeasy.eyebrow') }}</h3>
       <h2 class="kpis-title fade-in-section" style="--delay: 0.1s;">
         {{ $t('kpisWebeasy.title') }}<span class="gradient-text2">{{ $t('kpisWebeasy.title2') }}</span>
       </h2>
       <p class="fade-in-section" style="--delay: 0.2s;">
         {{ $t('kpisWebeasy.description') }}
       </p>
     </div>

     <div class="kpis-grid">
       <article v-for="(stat, index) in stats" :key="stat.label" class="kpi-card fade-in-section" :style="{ '--delay': `${0.15 + index * 0.08}s` }">
         <div class="kpi-glow" aria-hidden="true"></div>
         <div class="kpi-metric">
           <span class="kpi-value">{{ formatValue(stat, animatedValues[index]) }}</span>
           <span class="kpi-icon" aria-hidden="true">
             <svg viewBox="0 0 24 24" role="presentation" focusable="false">
               <defs>
                 <linearGradient :id="`kpi-icon-gradient-${index}`" x1="0%" y1="0%" x2="100%" y2="0%">
                   <stop offset="0%" stop-color="#5295FF" />
                   <stop offset="70%" stop-color="#7AF0FC" />
                 </linearGradient>
               </defs>
               <path :d="iconPath(stat.icon)" :stroke="`url(#kpi-icon-gradient-${index})`" />
             </svg>
           </span>
         </div>
         <span class="kpi-label">{{ stat.label }}</span>
       </article>
     </div>
   </div>
 </section>
</template>

<script>
export default {
 name: 'KpisWebeasy',
 data() {
   return {
     animatedValues: [0, 0, 0, 0],
     hasAnimated: false,
     observer: null,
   };
 },
 computed: {
   stats() {
     const locale = this.$i18n.locale;
     const labels = {
       en: {
         projects: this.$t('kpisWebeasy.stats.projects'),
         clients: this.$t('kpisWebeasy.stats.clients'),
         reviews: this.$t('kpisWebeasy.stats.reviews'),
         solutions: this.$t('kpisWebeasy.stats.solutions'),
       },
       fr: {
         projects: this.$t('kpisWebeasy.stats.projects'),
         clients: this.$t('kpisWebeasy.stats.clients'),
         reviews: this.$t('kpisWebeasy.stats.reviews'),
         solutions: this.$t('kpisWebeasy.stats.solutions'),
       },
     };
     const currentLabels = labels[locale] || labels.en;

     return [
       { target: 50, suffix: '+', label: currentLabels.projects, precision: 0, icon: 'projects' },
       { target: 98, suffix: '%', label: currentLabels.clients, precision: 0, icon: 'clients' },
       { target: 4.9, suffix: '/5', label: currentLabels.reviews, precision: 1, icon: 'reviews' },
       { target: 100, suffix: '%', label: currentLabels.solutions, precision: 0, icon: 'solutions' },
     ];
   },
 },
 mounted() {
   if ('IntersectionObserver' in window) {
     this.observer = new IntersectionObserver(
       (entries) => {
         if (entries.some((entry) => entry.isIntersecting)) {
           this.startAnimation();
           this.observer?.disconnect();
         }
       },
       { threshold: 0.35 }
     );
     this.observer.observe(this.$refs.kpisSection);
   } else {
     this.startAnimation();
   }
 },
 beforeUnmount() {
   this.observer?.disconnect();
 },
 methods: {
   startAnimation() {
     if (this.hasAnimated) return;
     this.hasAnimated = true;

     const duration = 4800;
     const startTime = performance.now();

     const animate = (now) => {
       const progress = Math.min((now - startTime) / duration, 1);
       const eased = 1 - Math.pow(1 - progress, 3);

       this.animatedValues = this.stats.map((stat) => {
         const value = stat.target * eased;
         if (stat.precision > 0) {
           return Number(value.toFixed(stat.precision));
         }
         return Math.round(value);
       });

       if (progress < 1) {
         requestAnimationFrame(animate);
       }
     };

     requestAnimationFrame(animate);
   },
   formatValue(stat, value) {
     return `${value}${stat.suffix}`;
   },
   iconPath(icon) {
     const paths = {
       projects: 'M4 16.5 9 11.5l3 3 7-7M14 7h5v5',
       clients: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-6.5 7a6.5 6.5 0 0 1 13 0',
       reviews: 'M12 3.6l2.3 4.7 5.2.8-3.7 3.6.9 5.1-4.7-2.5-4.7 2.5.9-5.1L4.6 9.1l5.2-.8z',
       solutions: 'M12 3a6 6 0 0 0-3.5 10.9V17h7v-3.1A6 6 0 0 0 12 3Zm-2 16h4M10.5 21h3',
     };

     return paths[icon] || paths.projects;
   },
 },
};
</script>

<style scoped>
.kpis-section {
 display: flex;
 justify-content: center;
 margin: 2rem 0 3rem;
}

.kpis-panel {
 background-image: url('@/assets/background.webp');
 background-size: cover;
 background-position: center;
 width: 100%;
 padding: 2.2rem;
 border-radius: 2rem;
 box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
 backdrop-filter: blur(18px);
 display: flex;
 flex-direction: column;
 gap: 1.8rem;
}

.kpis-intro {
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 0.75rem;
 max-width: 760px;
}

.kpis-eyebrow {
 margin: 0;
 text-transform: uppercase;
}

.kpis-title {
 margin: 0;
 color: #ffffff;
 font-size: clamp(1.7rem, 2vw + 1.1rem, 2.6rem);
 font-weight: 700;
 line-height: 1.15;
}

.kpis-description {
 margin: 0;
 color: rgba(255, 255, 255, 0.78);
 font-size: 1rem;
 line-height: 1.75;
 max-width: 700px;
}

.kpis-grid {
 display: grid;
 grid-template-columns: repeat(4, minmax(0, 1fr));
 gap: 1rem;
}

.kpi-card {
 position: relative;
 overflow: hidden;
 display: flex;
 flex-direction: column;
 gap: 0.5rem;
 padding: 1.4rem;
 border-radius: 1.25rem;
 background: rgba(8, 12, 24, 0.42);
 box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 24px rgba(0, 0, 0, 0.22);
 backdrop-filter: blur(5px);
}

.kpi-metric {
 position: relative;
 z-index: 1;
 display: flex;
 align-items: center;
 gap: 0.8rem;
}

.kpi-glow {
 position: absolute;
 inset: auto auto -30px -20px;
 width: 120px;
 height: 120px;
 border-radius: 50%;
 background: radial-gradient(circle, rgba(123, 140, 255, 0.35), transparent 70%);
 pointer-events: none;
}

.kpi-icon {
 display: inline-flex;
 align-items: center;
 justify-content: center;
 width: 2.35rem;
 height: 2.35rem;
 margin-top: 0.1rem;
 background: transparent;
 box-shadow: none;
}

.kpi-icon svg {
 width: 2.35rem;
 height: 2.35rem;
 fill: none;
 stroke-width: 1.7;
 stroke-linecap: round;
 stroke-linejoin: round;
}

.kpi-value {
 color: #ffffff;
 font-size: clamp(1.7rem, 1.3vw + 1.2rem, 2.3rem);
 font-weight: 700;
 letter-spacing: -0.02em;
 text-shadow: 0 0 24px rgba(123, 140, 255, 0.18);
}

.kpi-label {
 color: rgba(255, 255, 255, 0.72);
 font-size: 0.92rem;
 text-transform: uppercase;
 letter-spacing: 0.12em;
}

@media (max-width: 1024px) {
 .kpis-grid {
   grid-template-columns: repeat(2, minmax(0, 1fr));
 }
}

@media (max-width: 640px) {
 .kpis-panel {
   padding: 1.3rem;
   border-radius: 1.4rem;
 }

 .kpis-grid {
   grid-template-columns: 1fr;
 }

 .kpi-card {
   padding: 1.15rem;
 }

 .kpi-metric {
   gap: 0.65rem;
 }

 .kpi-icon {
   width: 2rem;
   height: 2rem;
 }

 .kpi-metric {
   gap: 0.6rem;
 }

 .kpi-icon {
   width: 2.5rem;
   height: 2.5rem;
 }
}

.fade-in-section {
 opacity: 0;
 transform: translateY(20px);
 animation: fadeInSection 1s ease-out forwards;
 animation-delay: var(--delay);
}

@keyframes fadeInSection {
 0% {
   opacity: 0;
   transform: translateY(20px);
 }
 100% {
   opacity: 1;
   transform: translateY(0);
 }
}
</style>
